import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { statePath } from "@/lib/stateRegistry";
import { Zap, MapPin, Users, Building2, ArrowRight, Info, Lock } from "lucide-react";
import { PhoneList, UtilityLinks } from "@/components/UtilityContact";
import { DIRECTORY, cardId } from "@/lib/utilitiesDirectory";
import {
  TX_MAP_VIEWBOX,
  TX_OUTLINE_PATH,
  TDU_TERRITORY_PATHS,
  TDU_AREA_SQMI,
  MAP_CITIES,
  TDU_DISPLAY,
  TDU_CODES,
  NON_CHOICE_TERRITORY_PATHS,
  NON_CHOICE_DISPLAY,
  NON_CHOICE_CODES,
} from "@/lib/texasTduMap";

/* -------------------------------------------------------------------------
 * Real Texas TDU service-territory map. Boundaries and the state outline are
 * sourced from public federal GIS layers (HIFLD "Electric Retail Service
 * Territories" + Census TIGERweb), not hand-drawn -- see the full sourcing
 * and honesty notes in frontend/src/lib/texasTduMap.js before editing.
 *
 * Shared between Utilities.jsx (variant="full") and Home.jsx (variant="home",
 * Ive-WBD placement spec 2026-09-30) so the ~300 lines of SVG/territory-
 * selection code and the TDU geometry only exist once -- the Entergy/
 * CenterPoint boundary correction that same day is exactly the kind of fix
 * that would silently drift between two copies otherwise. Same hover/focus/
 * click/keyboard selection behavior and aria-labels on both variants; only
 * the detail-panel field set differs (see the `compact` branches below).
 *
 * Caller is responsible for the surrounding `grid grid-cols-12 gap-8`
 * container -- this component renders its two grid children (the map card
 * and the detail aside) as siblings so both variants share one page-chrome
 * pattern instead of the map re-owning its own grid wrapper.
 * ---------------------------------------------------------------------- */

/** Fetches Texas's real statewide average residential rate once per mount (a static statewide
 * fact, not per-territory) -- same fail-quiet contract as BillCheck.jsx's TexasAverageComparison:
 * renders nothing on loading, fetch failure, or an `available: false` / non-finite source value.
 * Never re-fetched on territory clicks since selecting a different TDU doesn't remount this hook. */
function useTxResidentialRate() {
  const [value, setValue] = useState(null);

  useEffect(() => {
    let mounted = true;
    api
      .get("/market/tx-residential-rate")
      .then((r) => {
        if (mounted && r.data && r.data.available && Number.isFinite(r.data.value)) {
          setValue(r.data.value);
        }
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  return value;
}

/** Rate-context block (Decision 2): choice-TDU panels only, never the 4 non-choice ones -- the
 * number is circular there. Deliberately outside the grid-cols-2 stat-tile visual grammar
 * (Customers/Service-area = "this utility's own data"); this is a categorically different,
 * single real statewide figure, verbatim, or nothing -- never a derived per-TDU estimate. */
function TduRateContext({ value, utilityName }) {
  if (!Number.isFinite(value)) return null;
  return (
    <div className="mt-4 pt-4 border-t border-white/10 relative" data-testid="tdu-rate-context">
      <p className="flex items-center gap-1.5 text-xs text-slate-400 mb-1.5">
        <Info className="w-3.5 h-3.5" /> For reference
      </p>
      <p className="text-sm text-slate-300 leading-relaxed">
        Texas&rsquo;s statewide average residential rate is {value.toFixed(1)}&cent;/kWh (EIA). This is a statewide figure, not specific to {utilityName} &mdash; rates from providers in this area vary by plan.
      </p>
    </div>
  );
}

/** One utility's contact cell: phone numbers (short labels), verified date, links, and the directory-card link. */
function TduContactCell({ utility }) {
  return (
    <>
      <PhoneList utility={utility} tone="dark" compact testId="tdu-phones" />
      <p className="min-h-[1.25rem] text-xs text-slate-300" data-testid="tdu-verified">
        {utility.verified && utility.phones.length > 0 ? `Numbers last checked ${utility.verified}` : ""}
      </p>
      <div className="mt-2">
        <UtilityLinks utility={utility} tone="dark" compact testId="tdu-links" />
      </div>
      {utility.code && utility.inDirectory && (
        <Link
          to={`${statePath("texas", "utilities")}#${cardId(utility.code)}`}
          aria-label={`See the ${utility.name} directory card: all phone numbers, hours, and FAQs`}
          className="mt-1 inline-flex min-h-[44px] items-center gap-1.5 rounded-md text-sm font-semibold text-[#4ADE80] underline decoration-white/30 hover:decoration-[#4ADE80] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A2540]"
          data-testid="tdu-directory-card-link"
        >
          See the full directory card <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      )}
    </>
  );
}

/** Contact block for the selected territory, from the shared utility directory data (src/lib/utilitiesDirectory.js).
 * Hover, focus and tap all just change `active`. NO LAYOUT JUMP, by construction: every utility's cell is rendered in the
 * SAME grid cell and only the active one is visible, so the block is always as tall as the tallest utility's contact
 * details at the current panel width, whatever the label lengths or number counts. Inactive cells are visibility:hidden,
 * aria-hidden and inert, so they cannot be read, tabbed to, or tapped. */
function TduContact({ activeObj, active, directory }) {
  const cells = directory.utilities.map((u) => ({ ...u, inDirectory: true }));
  if (!directory.byCode[active]) {
    cells.push({ code: active, name: activeObj.name, phones: [], website: activeObj.website || null, outageUrl: null, outageMapUrl: null, verified: null, inDirectory: false });
  }
  return (
    <div className="mt-6 pt-6 border-t border-white/10 relative" data-testid="tdu-contact">
      <p className="text-overline text-slate-300 mb-1">Phone numbers</p>
      <div className="grid grid-cols-[minmax(0,1fr)]" data-testid="tdu-contact-stack">
        {cells.map((u) => {
          const isActive = u.code === active;
          return (
            <div
              key={u.code}
              style={{ gridArea: "1 / 1", visibility: isActive ? "visible" : "hidden" }}
              aria-hidden={isActive ? undefined : true}
              inert={isActive ? undefined : true}
              data-active={isActive ? "true" : "false"}
              data-contact-code={u.code}
            >
              <TduContactCell utility={u} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function TduTerritoryMap({ variant = "full", directory = DIRECTORY }) {
  const compact = variant === "home";
  const [active, setActive] = useState(TDU_CODES[0]);
  // A click, tap, Enter or Space "sticks" the territory: hovering or focusing others (for example while the cursor
  // travels across the map toward the details panel) no longer changes it. Another click picks a new one.
  const [locked, setLocked] = useState(false);
  const isChoice = Object.prototype.hasOwnProperty.call(TDU_DISPLAY, active);
  const activeObj = isChoice ? TDU_DISPLAY[active] : NON_CHOICE_DISPLAY[active];
  const txRate = useTxResidentialRate();
  const contact = activeObj ? <TduContact activeObj={activeObj} active={active} directory={directory} /> : null;

  /** Hover / focus: a live preview, ignored once a territory has been clicked. */
  function previewTerritory(code) {
    if (!locked) setActive(code);
  }

  /** Click / tap / Enter / Space: select and stick until another is clicked. */
  function selectTerritory(code) {
    setActive(code);
    setLocked(true);
  }

  function handleKeyDown(e, code) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      selectTerritory(code);
    }
  }

  return (
    <>
      {/* MAP */}
      <div className="col-span-1 min-w-0 lg:col-span-7">
        <div className="bg-white border border-slate-100 rounded-3xl p-6 md:p-8">
          <p className="text-overline text-slate-500 mb-1">Interactive map &middot; Texas</p>
          <p className="font-display text-xl font-bold text-[#0A2540] mb-4">Hover or tap a territory to explore</p>

          <svg viewBox={TX_MAP_VIEWBOX} className="w-full h-auto" data-testid="tdu-map" preserveAspectRatio="xMidYMid meet" role="group" aria-label="Map of Texas sectioned by transmission and distribution utility territory">
            <defs>
              <filter id="state-shadow" x="-5%" y="-5%" width="110%" height="110%">
                <feDropShadow dx="0" dy="4" stdDeviation="6" floodColor="#0A2540" floodOpacity="0.08" />
              </filter>
              <pattern id="hatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                <line x1="0" y1="0" x2="0" y2="6" stroke="#CBD5E1" strokeWidth="1" />
              </pattern>
              {/* HIFLD/EIA territory polygons are sourced independently from the Census TIGERweb
                  state outline, so their edges don't line up exactly -- without this clip, a
                  territory's fill/stroke can poke a sliver past the state line at the boundary.
                  Clipping every territory path to the real outline guarantees nothing ever renders
                  outside Texas, regardless of small source-data mismatches. */}
              <clipPath id="tx-clip">
                <path d={TX_OUTLINE_PATH} />
              </clipPath>
            </defs>

            {/* Texas base shape -- real Census TIGERweb state boundary */}
            <path d={TX_OUTLINE_PATH} fill="url(#hatch)" stroke="#94A3B8" strokeWidth="1.5" filter="url(#state-shadow)" />

            <g clipPath="url(#tx-clip)">
            {/* TDU territories -- real HIFLD/EIA boundary polygons, one per canonical TDU code */}
            {TDU_CODES.map((code) => {
              const d = TDU_TERRITORY_PATHS[code];
              const t = TDU_DISPLAY[code];
              const isActive = active === code;
              return (
                <path
                  key={code}
                  d={d}
                  fill={t.color}
                  fillOpacity={isActive ? 0.85 : 0.55}
                  stroke={t.color}
                  strokeWidth={isActive ? 2 : 1}
                  strokeOpacity={isActive ? 1 : 0.7}
                  strokeLinejoin="round"
                  className={`tdu-territory${isActive ? " is-active" : ""}`}
                  tabIndex={0}
                  role="button"
                  aria-label={`${t.name} service territory${isActive ? ", selected" : ""}`}
                  onMouseEnter={() => previewTerritory(code)}
                  onFocus={() => previewTerritory(code)}
                  onClick={() => selectTerritory(code)}
                  onKeyDown={(e) => handleKeyDown(e, code)}
                  data-testid={`tdu-region-${code}`}
                />
              );
            })}

            {/* Non-choice (regulated, no retail competition) territories -- real HIFLD boundary
                polygons, drawn on top of the choice territories above because Austin Energy and
                CPS Energy are real enclaves carved out of what would otherwise look like
                ERCOT-choice coverage (see sourcing notes in texasTduMap.js). Dashed outline is a
                deliberate visual cue, on top of the brown fill, that this is a different category
                of territory than the choice ones. */}
            {NON_CHOICE_CODES.map((code) => {
              const d = NON_CHOICE_TERRITORY_PATHS[code];
              const t = NON_CHOICE_DISPLAY[code];
              const isActive = active === code;
              return (
                <path
                  key={code}
                  d={d}
                  fill={t.color}
                  fillOpacity={isActive ? 0.75 : 0.45}
                  stroke={t.color}
                  strokeWidth={isActive ? 2 : 1.25}
                  strokeOpacity={isActive ? 1 : 0.75}
                  strokeDasharray="4 2"
                  strokeLinejoin="round"
                  className={`tdu-territory${isActive ? " is-active" : ""}`}
                  tabIndex={0}
                  role="button"
                  aria-label={`${t.name} service area, a regulated market with no retail electric choice${isActive ? ", selected" : ""}`}
                  onMouseEnter={() => previewTerritory(code)}
                  onFocus={() => previewTerritory(code)}
                  onClick={() => selectTerritory(code)}
                  onKeyDown={(e) => handleKeyDown(e, code)}
                  data-testid={`tdu-region-${code}`}
                />
              );
            })}
            </g>

            {/* State outline on top for a crisp edge */}
            <path d={TX_OUTLINE_PATH} fill="none" stroke="#0A2540" strokeWidth="2" strokeLinejoin="round" pointerEvents="none" />

            {/* City dots + labels */}
            {MAP_CITIES.map((c) => (
              <g key={c.name} pointerEvents="none">
                <circle cx={c.x} cy={c.y} r="3.5" fill="#0A2540" />
                <circle cx={c.x} cy={c.y} r="3.5" fill="white" opacity="0.35" />
                <text
                  x={c.x + 7}
                  y={c.y + 4}
                  fontSize="13"
                  fontWeight="600"
                  fill="#0A2540"
                  style={{ textShadow: "0 1px 0 rgba(255,255,255,0.9), 0 0 4px rgba(255,255,255,0.95)", paintOrder: "stroke", stroke: "white", strokeWidth: 3, strokeLinejoin: "round" }}
                >
                  {c.name}
                </text>
              </g>
            ))}

            {/* Compass (decorative) */}
            <g transform="translate(940, 60)" pointerEvents="none">
              <circle r="20" fill="white" stroke="#CBD5E1" strokeWidth="1.5" />
              <text y="-4" fontSize="11" textAnchor="middle" fontWeight="700" fill="#0A2540">N</text>
              <text y="13" fontSize="8" textAnchor="middle" fill="#94A3B8">↑</text>
            </g>
          </svg>

          {/* Legend -- also the tap target set for touch devices without hover */}
          <div className="mt-6 pt-6 border-t border-slate-100">
            <p className="text-xs font-semibold text-slate-500 mb-2">Areas with retail choice</p>
            <div className="flex flex-wrap gap-2">
              {TDU_CODES.map((code) => {
                const t = TDU_DISPLAY[code];
                const isActive = active === code;
                return (
                  <button
                    key={code}
                    type="button"
                    onClick={() => selectTerritory(code)}
                    onMouseEnter={() => previewTerritory(code)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${isActive ? "bg-[#0A2540] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                    data-testid={`tdu-legend-${code}`}
                  >
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: t.color }} />
                    {t.abbr}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-4">
            <p className="text-xs font-semibold text-slate-500 mb-2">Regulated — no retail choice</p>
            <div className="flex flex-wrap gap-2">
              {NON_CHOICE_CODES.map((code) => {
                const t = NON_CHOICE_DISPLAY[code];
                const isActive = active === code;
                return (
                  <button
                    key={code}
                    type="button"
                    onClick={() => selectTerritory(code)}
                    onMouseEnter={() => previewTerritory(code)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${isActive ? "bg-[#92400E] text-white" : "bg-amber-50 text-amber-900 hover:bg-amber-100"}`}
                    data-testid={`tdu-legend-${code}`}
                  >
                    <span className="w-2.5 h-2.5 rounded-full border border-dashed border-current" style={{ background: t.color }} />
                    {t.abbr}
                  </button>
                );
              })}
            </div>
          </div>

          <p className="flex items-start gap-2 mt-4 text-xs text-slate-500 leading-relaxed">
            <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" aria-hidden="true" />
            <span className="min-w-0" data-testid="tdu-map-footnote">
            {compact ? (
              <>
                Boundaries shown are simplified and approximate — for an exact answer at your address, use the <Link to={statePath("texas", "esid-lookup")} className="underline hover:text-[#16A34A]">ESID lookup</Link>.
              </>
            ) : (
              <>
                Territory boundaries shown here are simplified for a fast, readable map and are approximate near edges — they're sourced from public federal utility-territory data, not survey lines. For an exact answer at your address, use the <Link to={statePath("texas", "esid-lookup")} className="underline hover:text-[#16A34A]">ESID lookup</Link>.
              </>
            )}
            </span>
          </p>
        </div>
      </div>

      {/* DETAILS */}
      <aside className="col-span-1 min-w-0 lg:col-span-5">
        {activeObj && (
          <div className="bg-[#0A2540] text-white rounded-3xl p-8 relative overflow-hidden lg:sticky lg:top-24" data-testid="tdu-detail" data-choice={isChoice}>
            <div className="absolute -top-16 -right-16 w-52 h-52 rounded-full blur-3xl opacity-20" style={{ background: activeObj.color }} />
            <p className="sr-only" role="status" aria-live="polite">{activeObj.name} selected. Phone numbers are listed in this panel.</p>

            <div className="flex items-center gap-3 mb-2 relative">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: activeObj.color }}>
                {isChoice ? <Zap className="w-6 h-6 text-white" /> : <Lock className="w-6 h-6 text-white" />}
              </div>
              <div>
                <p className="text-xs text-slate-400">{isChoice ? "Serving utility (TDU)" : "Regulated utility — no retail choice"}</p>
                <h2 className="font-display text-2xl font-bold leading-tight" data-testid="tdu-detail-name">{activeObj.name}</h2>
              </div>
            </div>

            <p className="text-slate-300 mt-4 relative">{activeObj.coverageNote}</p>

            {isChoice && <TduRateContext value={txRate} utilityName={activeObj.name} />}

            {isChoice ? (
              compact ? (
                <>
                  {contact}
                  <Link to={`${statePath("texas", "plans")}?tdu=${active}`} className="btn-outline mt-7 w-full relative" data-testid="tdu-plans-cta">
                    Get notified when plan comparison launches <ArrowRight className="w-4 h-4" />
                  </Link>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4 mt-6 relative">
                    {activeObj.customers ? (
                      <div className="bg-white/5 rounded-xl p-4">
                        <p className="text-xs text-slate-400 flex items-center gap-1"><Users className="w-3 h-3" /> Customers</p>
                        <p className="font-display text-2xl font-bold mt-1">{(activeObj.customers / 1_000_000 >= 1 ? (activeObj.customers / 1_000_000).toFixed(1) + "M" : Math.round(activeObj.customers / 1000) + "K")}</p>
                      </div>
                    ) : (
                      <div className="bg-white/5 rounded-xl p-4">
                        <p className="text-xs text-slate-400 flex items-center gap-1"><Users className="w-3 h-3" /> Customers</p>
                        <p className="text-sm font-medium mt-2 text-slate-300">Not separately reported</p>
                      </div>
                    )}
                    <div className="bg-white/5 rounded-xl p-4">
                      <p className="text-xs text-slate-400 flex items-center gap-1"><MapPin className="w-3 h-3" /> Service area</p>
                      <p className="font-display text-2xl font-bold mt-1">{(TDU_AREA_SQMI[activeObj.code] / 1000).toFixed(0)}K<span className="text-xs font-normal text-slate-400"> mi²</span></p>
                    </div>
                  </div>

                  <div className="mt-6 pt-6 border-t border-white/10 relative">
                    <p className="text-overline text-slate-400 mb-2">Major cities served</p>
                    <div className="flex flex-wrap gap-1.5">
                      {activeObj.majorCities.map(c => (
                        <span key={c} className="text-xs px-2.5 py-1 rounded-full bg-white/10">{c}</span>
                      ))}
                    </div>
                  </div>

                  {contact}

                  <Link to={`${statePath("texas", "plans")}?tdu=${active}`} className="btn-outline mt-7 w-full relative" data-testid="tdu-plans-cta">
                    Get notified when plan comparison launches <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to={statePath("texas", "esid-lookup")} className="btn-outline mt-3 w-full relative" data-testid="tdu-esid-cta">
                    Find my ESID <ArrowRight className="w-4 h-4" />
                  </Link>
                </>
              )
            ) : compact ? (
              <div className="mt-6 pt-6 border-t border-white/10 relative" data-testid="tdu-non-choice-notice">
                <div className="flex items-start gap-3 bg-amber-900/30 border border-amber-500/30 rounded-xl p-4">
                  <Lock className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-100 leading-relaxed">
                    This is a <strong>regulated market with no retail electric choice</strong> — {activeObj.name} sells you electricity directly, so customers here do not choose a retail provider.
                  </p>
                </div>
                {contact}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4 mt-6 relative">
                  <div className="bg-white/5 rounded-xl p-4">
                    <p className="text-xs text-slate-400 flex items-center gap-1"><Users className="w-3 h-3" /> Customers</p>
                    <p className="font-display text-2xl font-bold mt-1">{(activeObj.customers / 1_000_000 >= 1 ? (activeObj.customers / 1_000_000).toFixed(1) + "M" : Math.round(activeObj.customers / 1000) + "K")}</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4">
                    <p className="text-xs text-slate-400 flex items-center gap-1"><Building2 className="w-3 h-3" /> Market type</p>
                    <p className="text-sm font-semibold mt-2">{activeObj.utilityType}</p>
                  </div>
                </div>

                {contact}

                <div className="mt-7 pt-6 border-t border-white/10 relative" data-testid="tdu-non-choice-notice">
                  <div className="flex items-start gap-3 bg-amber-900/30 border border-amber-500/30 rounded-xl p-4">
                    <Lock className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-100 leading-relaxed">
                      This is a <strong>regulated market with no retail electric choice</strong>. {activeObj.name} sells you electricity directly — customers here do not choose a retail provider, so plan comparison will not apply to this area.
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </aside>
    </>
  );
}
